import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'

import { type GridColDef, Pagination } from '@dhl/components'
import { Box, Button, Grid, IconButton, Paper } from '@dhl-ui/components'
import { EditSolidIcon, PlusIcon } from '@dhl-ui/icons'
import { getActionTypeLabel, useGetAdminActions, type ActionAdminDetails } from '@dhl/receiver-dhl-extra-api'

import { links } from '~/AppRoutes'
import { AdminDataGrid, lowercaseTransform, SearchTextField } from '~/common'

export const PromotionsPage = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [page, setPage] = useState<number>(0)
  const [pageSize, setPageSize] = useState<number>(10)
  const [searchText, setSearchText] = useState<string | undefined>(undefined)

  const { data: promotions, isLoading } = useGetAdminActions({
    query: {},
  })

  const items = useMemo(() => {
    if (!searchText) {
      return promotions ?? []
    }

    return promotions?.filter(
      ({ title, description }) =>
        title.toLowerCase().includes(searchText.toLowerCase()) ||
        description.toLowerCase().includes(searchText.toLowerCase())
    )
  }, [promotions, searchText])

  const handleSearchChange = useCallback((search: string) => {
    setPage(0)
    setSearchText(search)
  }, [])

  const onClickEdit = (row: ActionAdminDetails) => {
    navigate(links.editPromotionLink(row?.actionId))
  }

  const handleRowClick = (row: ActionAdminDetails) => {
    navigate(links.showPromotionDetailsLink(row?.actionId))
  }

  const renderCellEdit = (row: ActionAdminDetails) => (
    <IconButton aria-label="edit servicespoints" onClick={() => onClickEdit(row)}>
      <EditSolidIcon data-testid="edit-servicepoints" />
    </IconButton>
  )

  const formatDateTime = (value: string | undefined) => (value ? dayjs(value).format('DD MMMM YYYY') : '')

  const formatActionType = (value: string) => getActionTypeLabel(value)

  const columns: GridColDef<ActionAdminDetails>[] = [
    {
      field: 'title',
      headerName: t('admin.dhlExtraPage.promotionsPage.promotionHeader.title'),
      flex: 1,
    },
    {
      field: 'actionType',
      headerName: t('admin.dhlExtraPage.promotionsPage.promotionHeader.actionType'),
      flex: 1,
      valueGetter: formatActionType,
    },
    {
      field: 'startAt',
      headerName: t('admin.dhlExtraPage.promotionsPage.promotionHeader.startAt'),
      flex: 1,
      valueGetter: formatDateTime,
    },
    {
      field: 'endAt',
      headerName: t('admin.dhlExtraPage.promotionsPage.promotionHeader.endAt'),
      flex: 1,
      valueGetter: formatDateTime,
    },
    {
      field: 'actions',
      headerName: t('admin.servicePointVendorsPage.userHeader.actions'),
      sortable: false,
      flex: 1,
      renderCell: ({ row }) => renderCellEdit(row),
      disableReorder: true,
      disableExport: true,
      resizable: false,
      filterable: false,
      editable: false,
    },
  ]

  return (
    <>
      <Box mb={3}>
        <Grid container direction="row" justifyContent="space-between" alignItems="center">
          <Grid>
            <SearchTextField onChange={handleSearchChange} transform={lowercaseTransform} />
          </Grid>
          <Grid>
            <Button
              variant="contained"
              color="primary"
              data-testid="add-dhlExtra-promotion"
              onClick={() => navigate(links.addPromotionLink())}
              startIcon={<PlusIcon />}
            >
              {t('admin.dhlExtraPage.promotionsPage.promotionHeader.addPromotionButtonText')}
            </Button>
          </Grid>
        </Grid>
      </Box>
      <Box mt={2}>
        <Paper>
          <AdminDataGrid
            autoHeight
            disableColumnFilter
            disableRowSelectionOnClick
            hideFooter
            columnHeaderHeight={48}
            columns={columns}
            data-testid="dhlExtra-promotions-table"
            getRowId={(row) => row.actionId}
            loading={isLoading}
            paginationModel={{
              page,
              pageSize,
            }}
            rows={items ?? []}
            rowHeight={48}
            onRowClick={({ row }) => handleRowClick(row)}
          />
        </Paper>
        <Box mt={2}>
          <Pagination
            page={page}
            count={items?.length ?? 0}
            onPageChange={(_, page) => setPage(page)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={setPageSize}
          />
        </Box>
      </Box>
    </>
  )
}
